import React, { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { Checkbox, Icon, IconButton, Status, Tooltip, Typography } from 'shared/ui'
import { LINK_HELP_QUIET_HOURS } from 'shared/constants/links'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { openQuietHoursSettings } from 'entities/QuietHours'
import { useBroadcastViewContext } from 'pages/broadcasts/ui/BroadcastView/context/broadcastViewContext'
import styles from './styles.module.scss'

export const QuietHoursContent = observer(() => {
  const { usersStore } = useEntitiesContext()
  const {
    enableQuietHours,
    setEnableQuietHours,
    isQuietHoursEnabled,
    isQuietHoursBlockOnWeekend,
    quietHoursAllowedTimeFrom,
    quietHoursAllowedTimeTo,
    quietHoursClosureStore,
  } = useBroadcastViewContext()
  const [isHiddenByUser, setIsHiddenByUser] = useState(false)
  const user = usersStore.user
  const isRoleAllowed = !!(
    user?.isOwnerRole ||
    user?.isAdminRole ||
    user?.isManagerRole ||
    (user?.isMemberRole && isQuietHoursEnabled)
  )

  useEffect(() => {
    quietHoursClosureStore?.loadClosureCounter()
  }, [quietHoursClosureStore?.loadClosureCounter])

  const onClose = () => {
    quietHoursClosureStore?.incrementClosureCounter()
    setIsHiddenByUser(true)
  }

  if (!isRoleAllowed) return null
  if (!isQuietHoursEnabled && (quietHoursClosureStore?.shouldHideBanner || isHiddenByUser))
    return null

  return (
    <div
      className={styles.wrap}
      onClick={() => {
        if (!enableQuietHours && !isQuietHoursEnabled) {
          openQuietHoursSettings()
        } else {
          setEnableQuietHours(!enableQuietHours)
        }
      }}
    >
      <div className={styles.left}>
        <div onClick={(e) => e.stopPropagation()} role='presentation'>
          <Checkbox
            checked={enableQuietHours}
            onChange={(value) => {
              if (value && !isQuietHoursEnabled) {
                openQuietHoursSettings()
              } else {
                setEnableQuietHours(value)
              }
            }}
            size={16}
            style={{ cursor: 'pointer' }}
          />
        </div>
        <Typography
          variant={'text-input-field'}
          color={'var(--content-primary-primary)'}
          ariaLabel={'EnableQuietHours'}
        >
          Enable quiet hours
        </Typography>
        <Tooltip
          label={
            <div
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              role='presentation'
            >
              <Typography
                variant={'body-md-regular'}
                color={'var(--content-primary-primary-inverted)'}
                ariaLabel={'EnableQuiteHoursTooltip'}
              >
                {isQuietHoursEnabled ? (
                  <>
                    Quiet hours
                    <br />
                    <Typography
                      variant={'body-sm-regular'}
                      color={'var(--content-primary-tertiary-inverted)'}
                      ariaLabel={'EnableQuiteHoursTooltip'}
                    >
                      Monday - {isQuietHoursBlockOnWeekend ? 'Friday' : 'Sunday'}:{' '}
                      {quietHoursAllowedTimeFrom} - {quietHoursAllowedTimeTo}
                      <br />
                      {isQuietHoursBlockOnWeekend && <>Saturday - Sunday: Don’t send</>}
                    </Typography>
                  </>
                ) : (
                  <>
                    Delay sending messages between specific hours to
                    <br /> avoid contacting recipients at inappropriate times.
                    <br />
                    <a href={`${LINK_HELP_QUIET_HOURS}`} target='_blank' rel='noreferrer'>
                      Learn more
                    </a>
                  </>
                )}
              </Typography>
            </div>
          }
          placement={'top'}
          width={!isQuietHoursEnabled ? 342 : undefined}
        >
          <div
            className={styles.info}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role='button'
            tabIndex={0}
          >
            <Icon icon={'question'} fontSize={12} />
          </div>
        </Tooltip>
        {!isQuietHoursEnabled && (
          <Status title={'Beta'} intent={'warning'} emphasis={'high'} size={'label'} />
        )}
      </div>
      {!isQuietHoursEnabled && (
        <div className={styles.right}>
          <IconButton
            ariaLabel={'QuietHoursClose'}
            icon={'close'}
            size={'small'}
            variant={'icon'}
            color={'tertiary'}
            onClick={(e) => {
              e.stopPropagation()
              onClose()
            }}
          />
        </div>
      )}
    </div>
  )
})
